import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { AcuityCategory } from 'src/shared/models/acuity-category.model';
import { AcuityCensusRecord } from 'src/shared/models/acuity-census-record.model';
import { PageInterface } from 'src/app/workflow/planner/page/page.interface';
import { RecommendationService } from 'src/shared/services/recommendation.service';
import { AcuityShiftRecordService } from 'src/shared/services/shift-record-acuity.service';
import { ShiftRecordStatus } from 'src/shared/models/shift-record-status';
import { WorkflowPage } from 'src/shared/models/workflow-page.model';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { debounceTime } from 'rxjs/operators';
import { AcuityUtility } from '../acuity.util';
import { AncillaryEnterCurrentBaseComponent } from '../../base-classes/ancillary-enter-current-base.component';
import { Constants } from 'src/shared/utilities/constants.util';

@Component({
    selector: 'smt-enter-current',
    templateUrl: './enter-current.component.html',
    styleUrls: ['./enter-current.component.scss']
})
export class AcuityEnterCurrentComponent extends AncillaryEnterCurrentBaseComponent implements OnInit, PageInterface {
    @Input() page: WorkflowPage;
    @Output() previousRequested = new EventEmitter();

    public acuityCensusTotal = 0;    
    public acuityForm = new UntypedFormGroup({});
    public acuityFormValues$;
    public acuityCategories: AcuityCategory[];
    public formGroups: UntypedFormGroup[];
    public acuityCensusRecords: AcuityCensusRecord[];
    loading: boolean;
    
    constructor(
        protected _recommendationService: RecommendationService,
        protected _router: Router, 
        protected _acuityShiftRecordService: AcuityShiftRecordService,
        protected _workflowUtilityService: WorkflowUtilityService,
        protected _acuityUtil: AcuityUtility) {
        super(_recommendationService, _router, _workflowUtilityService);
    }

    ngOnInit() {        
        super.initialize(this.page, () => this.initialize());
    }

    getAcuityCensus(acuityCategoryId: number): string {
        if (!this.acuityCensusRecords ||
            !this.acuityCensusRecords.some(a => a.acuityCategoryId == acuityCategoryId)) {
            return '';
        }

        return this.acuityCensusRecords.find(a => a.acuityCategoryId == acuityCategoryId).patients.toString();
    }

    getAcuityControlName(acuityId: number): string {
        return this._acuityUtil.getAcuityControlName(acuityId);
    }

    initialize() { 
        this.shiftRecord = this.page.shiftRecord;
        this.acuityCensusRecords = [];

        this.initializeAcuityForm();
        this.initializeStaffForm();
        
        this.formGroups = [ this.acuityForm, this.staffForm ];
    }

    private async initializeAcuityForm() {        
        let costCenter = this.shiftRecord.costCenter;

        this.acuityCensusRecords = this.shiftRecord.acuityCensusRecords;

        if (WorkflowPageUtil.hasAcuityConfiguration(costCenter)) {
            this.acuityCategories = this._workflowUtilityService.buildAcuityCategories(this.shiftRecord);
        }      
        
        if (this.acuityCategories) {            
            this.acuityCategories.forEach(a => {
                this.acuityForm.addControl(this.getAcuityControlName(a.id), new UntypedFormControl(null, [Validators.required, Validators.min(0)]));
            });
        }        

        this.acuityCensusTotal = 0;

        if (this.acuityCensusRecords && this.acuityCensusRecords.length){
            this.acuityCensusRecords.forEach(acuityCensusRecord => {
                this.acuityForm.get(this.getAcuityControlName(acuityCensusRecord.acuityCategoryId)).setValue(acuityCensusRecord.patients);
                this.acuityCensusTotal += acuityCensusRecord.patients;
            });
        } 
        
        this.updateAcuityStaffingRecommendation();
        
        this.acuityFormValues$ = this.acuityForm.valueChanges.pipe(
            debounceTime(Constants.DebounceTime)
        ).subscribe(async values => {            
            this.acuityCensusTotal = this._acuityUtil.getCensusTotal(values, this.shiftRecord.id, this.acuityCategories, this. acuityCensusRecords);
            this.updateAcuityStaffingRecommendation();
        });
    }

    private updateAcuityStaffingRecommendation() {
        this.updateStaffingRecommendation(this._recommendationService.makeAcuityRecommendation(this.shiftRecord.shiftBlockId, this.shiftRecord.workflowTimeFacility, this.acuityCensusRecords));
    }
 
    valueOutOfRange(acuityCategoryId: number) : boolean {
        return this._acuityUtil.valueOutOfRange(acuityCategoryId, this.acuityForm);
    }
    
    controlValueOutOfRange(staffFormId) : boolean {
        let control = this.staffForm.get(this.getStaffControlName(staffFormId));
        return this._workflowUtilityService.valueOutOfRange(control,this.minValue,this.maxValue);
    }
    
    isFormValid(): boolean {
        return this.acuityForm && this.acuityForm.valid && this.staffForm && this.staffForm.valid && this.isValidStaffControls();
    }  

    save(): Promise<number> {
        this.setAcuityCensus();
        this.setPatientCensus();
        this.setStaffPlans();
        this.setStatus();
        
        return this._acuityShiftRecordService.setShiftRecordWithAssociated(this.shiftRecord);
    }

    private setAcuityCensus() {        
        this.shiftRecord.acuityCensusRecords = this.acuityCensusRecords;
    }

    private setPatientCensus() {
        this.shiftRecord.patientCensusRecord.patients = this.acuityCensusTotal;
    }

    private setStatus() {
        if (this.shiftRecord.status !== ShiftRecordStatus.Done && this.page.nextPage) {
            this.shiftRecord.status = this.page.nextPage.completedStatus;
        }
    }

    shouldSkipSave(): boolean {
        return false;
    }
}
